import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Avatar,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Switch,
} from '@mui/material';
import {
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon,
  DirectionsCar as CarIcon,
  AccountBalanceWallet as WalletIcon,
  Verified as VerifiedIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  DriveEta as DriverIcon,
  CameraAlt as CameraIcon,
  Help as HelpIcon,
  Support as SupportIcon,
  Info as InfoIcon,
  Luggage as LuggageIcon,
  Logout as LogoutIcon,
  Delete as DeleteIcon,
  Shield as ShieldIcon,
  Policy as PolicyIcon,
  NotificationsActive as NotificationsIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLoading } from '../../contexts/LoadingContext';
import apiService from '../../services/apiService';
import { getFullImageUrl } from '../../utils/helpers';
import { useTranslation } from 'react-i18next';
import TranslatingText from '../../components/Common/TranslatingText';
import { CircularProgress } from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import LocationAutocomplete, { LocationData } from '../../components/Common/LocationAutocomplete';

const LANGUAGE_OPTIONS = [
  'Arabic',
  'Bengali',
  'Cantonese',
  'Croatian',
  'Dutch',
  'English',
  'Farsi (Dari)',
  'Farsi (Persian)',
  'Filipino (Tagalog)',
  'French',
  'German',
  'Greek',
  'Gujarati',
  'Hindi',
  'Italian',
  'Japanese',
  'Korean',
  'Mandarin',
  'Polish',
  'Portuguese',
  'Punjabi',
  'Romanian',
  'Russian',
  'Serbian',
  'Spanish',
  'Tamil',
  'Turkish',
  'Ukrainian',
  'Urdu',
  'Vietnamese',
].sort();

interface ProfileStats {
  totalTripsAsDriver: number;
  totalTripsAsPassenger: number;
  driverRating: number;
  passengerRating: number;
  creditsBalance: number;
  totalEarned: number;
  pendingPayouts: number;
  clearedPayouts: number;
  disputedPayouts: number;
}

const ProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, updateUser, logout, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setLoading } = useLoading();

  const [isEditing, setIsEditing] = useState(false);

  // Stripe Connect Express State
  const stripeStatus = searchParams.get('stripe_status');
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutError, setPayoutError] = useState('');
  const [payoutSuccess, setPayoutSuccess] = useState('');

  const checkPayoutStatus = useCallback(async (showMessages = true) => {
    setPayoutLoading(true);
    setPayoutError('');
    setPayoutSuccess('');
    try {
      const response = await apiService.post('/payments/connect/status', {});
      if (response.success) {
        await refreshUserData(); // Always refresh user data

        if (showMessages) {
          if (response.data.stripeConnectOnboarded) {
            setPayoutSuccess(t('profilePage.payouts.onboardingSuccessMsg', 'Bank account linked successfully! You can now accept credit card payouts.'));
          } else if (response.data.details_submitted && (!response.data.charges_enabled || !response.data.payouts_enabled)) {
            setPayoutError(t('profilePage.payouts.onboardingPendingMsg', 'Stripe is still processing your information. This may take a few moments. Click "Refresh Status" to check again.'));
          } else {
            setPayoutError(t('profilePage.payouts.onboardingIncompleteMsg', 'Stripe onboarding was not completed. Please try again.'));
          }
        }
      }
    } catch (err: any) {
      console.error('Error checking payout status:', err);
      if (showMessages) {
        setPayoutError(err.message || 'Failed to check payout status.');
      }
    } finally {
      setPayoutLoading(false);
    }
  }, [refreshUserData, t]);

  useEffect(() => {
    if (stripeStatus === 'success' || stripeStatus === 'refresh') {
      checkPayoutStatus();
      // Clear the query params after checking
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('stripe_status');
      newParams.delete('tab');
      setSearchParams(newParams);
    }
  }, [stripeStatus, checkPayoutStatus, searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('tab') === 'payouts') {
      // Small timeout to ensure rendering is complete
      const timer = setTimeout(() => {
        const element = document.getElementById('payout-settings-section');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const handleLinkPayout = async () => {
    setPayoutLoading(true);
    setPayoutError('');
    try {
      const response = await apiService.post('/payments/connect/onboard', {});
      if (response.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setPayoutError('Failed to generate Stripe onboarding link.');
      }
    } catch (err: any) {
      console.error('Error initiating payout onboarding:', err);
      setPayoutError(err.message || 'Failed to initiate payout onboarding.');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleManagePayout = async () => {
    setPayoutLoading(true);
    setPayoutError('');
    try {
      const response = await apiService.get('/payments/connect/payout-login');
      if (response.success && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        setPayoutError('Failed to generate Stripe login link.');
      }
    } catch (err: any) {
      console.error('Error opening Stripe dashboard:', err);
      setPayoutError(err.message || 'Failed to open Stripe dashboard.');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleDisconnectPayout = async () => {
    if (!window.confirm(t('profilePage.payouts.disconnectConfirm', 'Are you sure you want to disconnect your Stripe account? This will prevent you from offering rides with Credit Card payments until you link a bank account again.'))) {
      return;
    }
    setPayoutLoading(true);
    setPayoutError('');
    setPayoutSuccess('');
    try {
      const response = await apiService.delete('/payments/connect/disconnect');
      if (response.success) {
        setPayoutSuccess(t('profilePage.payouts.disconnectSuccess', 'Stripe account disconnected successfully.'));
        await refreshUserData();
      } else {
        setPayoutError(response.message || 'Failed to disconnect Stripe account.');
      }
    } catch (err: any) {
      console.error('Error disconnecting payout:', err);
      setPayoutError(err.message || 'Failed to disconnect Stripe account.');
    } finally {
      setPayoutLoading(false);
    }
  };

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editErrors, setEditErrors] = useState<{ [key: string]: string }>({});
  const [editForm, setEditForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    preferredName: user?.preferredName || '',
    phoneNumber: user?.phoneNumber || '',
    bio: user?.bio || '',
    languages: (user as any)?.languages || [],
  });
  const [moderationStatus, setModerationStatus] = useState<'idle' | 'checking' | 'approved' | 'flagged'>('idle');
  const [moderationReason, setModerationReason] = useState('');
  const [stats, setStats] = useState<ProfileStats>({
    totalTripsAsDriver: 0,
    totalTripsAsPassenger: 0,
    driverRating: 0,
    passengerRating: 0,
    creditsBalance: 0,
    totalEarned: 0,
    pendingPayouts: 0,
    clearedPayouts: 0,
    disputedPayouts: 0,
  });

  // Service Area states
  const [rideRequestAlerts, setRideRequestAlerts] = useState(false);
  const [serviceCities, setServiceCities] = useState<any[]>([]);
  const [serviceAreaLoading, setServiceAreaLoading] = useState(false);
  const [showAddCityModal, setShowAddCityModal] = useState(false);
  const [selectedNewLocation, setSelectedNewLocation] = useState<LocationData | null>(null);
  const [addingCity, setAddingCity] = useState(false);

  // Service Area handlers
  const loadServiceAreas = useCallback(async () => {
    try {
      setServiceAreaLoading(true);
      const res = await apiService.get('/driver/service-areas');
      if (res.success) {
        setRideRequestAlerts(res.data.rideRequestAlerts);
        setServiceCities(res.data.locations || res.data.cities || []);
      }
    } catch (err) {
      console.error('Load service areas error:', err);
    } finally {
      setServiceAreaLoading(false);
    }
  }, []);

  const handleToggleAlerts = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    try {
      setServiceAreaLoading(true);
      const res = await apiService.put('/driver/service-areas/toggle', { enabled: value });
      if (res.success) {
        setRideRequestAlerts(value);
        if (!value) setServiceCities([]);
      } else {
        alert(res.message || 'Failed to update preference');
      }
    } catch (err) {
      alert('Failed to update preference');
    } finally {
      setServiceAreaLoading(false);
    }
  };

  const handleAddCity = async () => {
    if (!selectedNewLocation?.name || !selectedNewLocation?.latitude || !selectedNewLocation?.longitude) {
      alert('Please select a valid location from the suggestions');
      return;
    }
    try {
      setAddingCity(true);
      const res = await apiService.post('/driver/service-areas', {
        placeId:  selectedNewLocation.id,
        lat:      selectedNewLocation.latitude,
        lng:      selectedNewLocation.longitude,
        name:     selectedNewLocation.name,
        province: selectedNewLocation.province,
      });
      if (res.success) {
        setServiceCities(prev => [...prev, res.data]);
        setSelectedNewLocation(null);
        setShowAddCityModal(false);
      } else {
        alert(res.message || 'Could not add location');
      }
    } catch (err) {
      alert('Could not add location');
    } finally {
      setAddingCity(false);
    }
  };

  const handleRemoveCity = async (serviceAreaId: string, locationName: string) => {
    if (!window.confirm(t('profilePage.driverPreferences.removeConfirm', 'Remove {{name}} from your service area?', { name: locationName }))) {
      return;
    }
    try {
      const res = await apiService.delete(`/driver/service-areas/${serviceAreaId}`);
      if (res.success) {
        setServiceCities(prev => prev.filter(c => c.serviceAreaId !== serviceAreaId));
      } else {
        alert(res.message || 'Could not remove location');
      }
    } catch (err) {
      alert('Could not remove location');
    }
  };

  const handleCheckBioModeration = async () => {
    if (!editForm.bio.trim()) return;

    setModerationStatus('checking');
    try {
      const response = await apiService.post('/ai/moderate', {
        text: editForm.bio,
        context: 'user_bio'
      });

      if (response.success) {
        if (response.data.flagged) {
          setModerationStatus('flagged');
          setModerationReason(response.data.reason);
        } else {
          setModerationStatus('approved');
        }
      }
    } catch (error) {
      console.error('Moderation error:', error);
      setModerationStatus('idle');
    }
  };

  useEffect(() => {
    // Only load stats if user is authenticated and has a token
    const token = localStorage.getItem('rideclub_token');
    if (!user || !token) return;

    const loadStats = async () => {
      try {
        const [driverStatsRes, passengerStatsRes, creditsRes] = await Promise.all([
          apiService.get('/auth/driver-stats').catch(() => ({ success: false, data: {} })),
          apiService.get('/auth/passenger-stats').catch(() => ({ success: false, data: {} })),
          apiService.get('/credits/balance').catch(() => ({ success: false, data: {} }))
        ]);

        setStats({
          totalTripsAsDriver: driverStatsRes.data?.totalTrips || 0,
          totalTripsAsPassenger: passengerStatsRes.data?.totalTrips || 0,
          driverRating: driverStatsRes.data?.averageRating || 0,
          passengerRating: passengerStatsRes.data?.averageRating || 0,
          creditsBalance: creditsRes.data?.balance || 0,
          totalEarned: driverStatsRes.data?.totalEarned || 0,
          pendingPayouts: driverStatsRes.data?.pendingPayouts || 0,
          clearedPayouts: driverStatsRes.data?.clearedPayouts || 0,
          disputedPayouts: driverStatsRes.data?.disputedPayouts || 0,
        });
      } catch (error) {
        console.error('Load stats error:', error);
        // Don't throw error, just use default values
        setStats({
          totalTripsAsDriver: 0,
          totalTripsAsPassenger: 0,
          driverRating: 0,
          passengerRating: 0,
          creditsBalance: 0,
          totalEarned: 0,
          pendingPayouts: 0,
          clearedPayouts: 0,
          disputedPayouts: 0,
        });
      }
    };

    loadStats();
  }, []);

  useEffect(() => {
    if (user && user.role === 'DRIVER') {
      loadServiceAreas();
    }
  }, [user, loadServiceAreas]);

  if (!user) {
    return (
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Container maxWidth="lg">
          <Alert severity="error">{t('profilePage.alerts.loginToView', 'Please log in to view your profile.')}</Alert>
        </Container>
      </Box>
    );
  }

  const handleEditProfile = () => {
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      preferredName: user.preferredName || '',
      phoneNumber: user.phoneNumber || '',
      bio: user.bio || '',
      languages: (user as any)?.languages || [],
    });
    setEditErrors({});
    setEditDialogOpen(true);
  };

  const handleFormChange = (field: string, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    if (editErrors[field]) {
      setEditErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (field === 'bio') {
      setModerationStatus('idle');
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!editForm.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (editForm.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!editForm.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (editForm.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    if (editForm.bio && editForm.bio.length > 500) {
      errors.bio = 'Bio must be less than 500 characters';
    }

    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateForm()) return;

    setLoading(true, 'Updating profile...');
    try {
      await updateUser({
        preferredName: editForm.preferredName.trim(),
        phoneNumber: editForm.phoneNumber.trim(),
        bio: editForm.bio.trim() || undefined,
        ...(editForm.languages && { languages: editForm.languages }),
      } as any);

      setEditDialogOpen(false);
      alert(t('profilePage.alerts.updateSuccess', 'Profile updated successfully!'));
    } catch (error: any) {
      console.error('Update profile error:', error);

      const moderationError = apiService.handleModerationError(error);
      if (moderationError.isModerationError) {
        alert(moderationError.message);
      } else {
        alert(error.message || t('profilePage.alerts.updateError', 'Failed to update profile. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return t('common.notProvided', 'Not provided');
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const getInitials = () => {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  };

  const handleLogout = () => {
    if (window.confirm(t('profilePage.logoutConfirm', 'Are you sure you want to logout?'))) {
      logout();
    }
  };


  const handleChangeEmail = () => {
    // For now, redirect to support page as email change requires more verification
    navigate('/support');
  };

  const handleProfilePictureClick = () => {
    console.log('Profile picture button clicked');
    // Create a hidden file input and trigger click
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/jpg,image/webp';
    input.onchange = async (e: any) => {
      console.log('File selected:', e.target.files?.[0]);
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }

      setLoading(true, 'Uploading profile picture...');
      try {
        const formData = new FormData();
        formData.append('profilePicture', file);

        const response = await apiService.uploadFile('/auth/profile/picture', formData);

        if (response.success) {
          // Refresh user data to get the updated profile picture
          await refreshUserData();
          alert(t('profilePage.alerts.pictureSuccess', 'Profile picture updated successfully!'));
        } else {
          alert(response.message || 'Failed to upload profile picture');
        }
      } catch (error: any) {
        console.error('Upload profile picture error:', error);
        alert(error.response?.data?.message || error.message || 'Failed to upload profile picture');
      } finally {
        setLoading(false);
      }
    };
    input.click();
  };

  const handleDeleteProfilePicture = async () => {
    if (!user.profilePicture) {
      alert('No profile picture to delete');
      return;
    }

    if (!window.confirm(t('profilePage.alerts.pictureDeleteConfirm', 'Are you sure you want to delete your profile picture?'))) {
      return;
    }

    setLoading(true, 'Deleting profile picture...');
    try {
      const response = await apiService.delete('/auth/profile/picture');

      if (response.success) {
        await refreshUserData();
        alert(t('profilePage.alerts.pictureDeleteSuccess', 'Profile picture deleted successfully!'));
      } else {
        alert(response.message || 'Failed to delete profile picture');
      }
    } catch (error: any) {
      console.error('Delete profile picture error:', error);
      alert(error.response?.data?.message || error.message || 'Failed to delete profile picture');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, md: 3 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2.25rem', md: '3rem' },
            }}
          >
            {t('profilePage.title', '👤 My Profile')}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon sx={{ fontSize: '1.8rem !important' }} />}
            onClick={() => navigate('/settings')}
            sx={{ fontSize: '1.5rem', py: 1.5, px: 3, borderRadius: '12px' }}
          >
            {t('profilePage.settings', 'Settings')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Profile Card */}
          <Box sx={{ flex: { xs: '1', md: '0 0 33%' } }}>
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                <Avatar
                  src={getFullImageUrl(user.profilePicture) || undefined}
                  alt={`${user.firstName} ${user.lastName}`}
                  sx={{
                    width: 180,
                    height: 180,
                    fontSize: '4.5rem',
                    bgcolor: 'primary.main',
                  }}
                >
                  {getInitials()}
                </Avatar>
                <IconButton
                  onClick={handleProfilePictureClick}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 36,
                    height: 36,
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                  size="small"
                  title="Change profile picture"
                >
                  <CameraIcon fontSize="small" />
                </IconButton>
                {user.profilePicture && (
                  <IconButton
                    onClick={handleDeleteProfilePicture}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'error.main',
                      color: 'white',
                      width: 28,
                      height: 28,
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                    size="small"
                    title="Delete profile picture"
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '2.25rem' }}>
                {user.firstName} {user.lastName}
              </Typography>

              {user.memberId && (
                <Typography variant="subtitle1" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '1.4rem', fontWeight: 'bold', mb: 1.5 }}>
                  {user.memberId}
                </Typography>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={user.role === 'DRIVER' ? <DriverIcon sx={{ fontSize: '1.5rem !important' }} /> : <LuggageIcon sx={{ fontSize: '1.5rem !important' }} />}
                  label={user.role === 'DRIVER' ? t('profilePage.driver', 'Driver') : t('profilePage.passenger', 'Passenger')}
                  color={user.role === 'DRIVER' ? 'primary' : 'default'}
                  sx={{ fontSize: '1.2rem', py: 2, px: 1.5, height: 'auto', fontWeight: 'bold' }}
                />
              </Box>

              <Button
                variant="contained"
                startIcon={<EditIcon sx={{ fontSize: '1.8rem !important' }} />}
                fullWidth
                onClick={handleEditProfile}
                sx={{ mb: 3, fontSize: '1.5rem', py: 1.5 }}
              >
                {t('profilePage.editProfile', 'Edit Profile')}
              </Button>

              <Divider sx={{ my: 2 }} />

              {/* Stats */}
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2, gap: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.8rem' }}>
                    {stats.totalTripsAsDriver + stats.totalTripsAsPassenger}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.3rem', fontWeight: 600 }}>
                    {t('profilePage.rides', 'Rides')}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.8rem' }}>
                    {stats.driverRating > 0 ? stats.driverRating.toFixed(1) : t('profilePage.noRating', 'N/A')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.3rem', fontWeight: 600 }}>
                    {t('profilePage.rating', 'Rating')}
                  </Typography>
                </Box>
              </Box>

              {/* Credits - Only for Drivers */}
              {user.role === 'DRIVER' && (
                <Card variant="outlined" sx={{ mt: 2, bgcolor: 'background.default' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1 }}>
                      <WalletIcon color="primary" sx={{ fontSize: '2rem' }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.8rem' }}>
                        ${stats.creditsBalance.toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.3rem', mb: 1.5, fontWeight: 600 }}>
                      {t('profilePage.availableCredits', 'Available Credits')}
                    </Typography>
                    <Button
                      size="large"
                      onClick={() => navigate('/credits')}
                      sx={{ mt: 1, fontSize: '1.2rem', fontWeight: 'bold' }}
                    >
                      {t('profilePage.manageCredits', 'Manage Credits')}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </Paper>
          </Box>

          {/* Profile Details */}
          <Box sx={{ flex: 1 }}>
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 4, fontSize: '1.875rem' }}>
                {t('profilePage.yourStats', '📊 Your Stats')}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <Box>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h3" sx={{ mb: 1 }}>🚗</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.8rem' }}>
                        {stats.driverRating > 0 ? `⭐ ${stats.driverRating.toFixed(1)}` : t('profilePage.noRating', 'No rating')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
                        {t('profilePage.driverRating', 'Driver Rating')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                <Box>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Typography variant="h3" sx={{ mb: 1 }}>🎒</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.8rem' }}>
                        {stats.passengerRating > 0 ? `⭐ ${stats.passengerRating.toFixed(1)}` : t('profilePage.noRating', 'No rating')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
                        {t('profilePage.passengerRating', 'Passenger Rating')}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                {/* Credits and Earnings - Only for Drivers */}
                {user.role === 'DRIVER' && (
                  <>
                    <Box>
                      <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate('/credits')}>
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <Typography variant="h3" sx={{ mb: 1 }}>💳</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.8rem' }}>
                            ${stats.creditsBalance.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
                            {t('profilePage.availableCredits', 'Credits')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>

                    <Box>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <Typography variant="h3" sx={{ mb: 1 }}>💰</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.8rem' }}>
                            ${stats.totalEarned.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
                            {t('profilePage.totalEarned', 'Total Earned')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  </>
                )}
              </Box>
            </Paper>

            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 4, fontSize: '1.875rem' }}>
                {t('profilePage.profileInformation', 'Profile Information')}
              </Typography>

              <List>
                <ListItem
                  secondaryAction={
                    <Button size="small" onClick={handleChangeEmail}>
                      {t('profilePage.change', 'Change')}
                    </Button>
                  }
                >
                  <ListItemIcon>
                    <EmailIcon color="primary" sx={{ fontSize: '2rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{t('profilePage.emailAddress', 'Email Address')}</Typography>}
                    secondary={<Typography color="text.secondary" sx={{ fontSize: '1.3rem' }}>{user.email}</Typography>}
                  />
                </ListItem>

                <Divider />

                <ListItem
                  secondaryAction={
                    <Button size="small" onClick={handleEditProfile}>
                      {t('profilePage.edit', 'Edit')}
                    </Button>
                  }
                >
                  <ListItemIcon>
                    <PhoneIcon color="primary" sx={{ fontSize: '2rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{t('profilePage.phoneNumber', 'Phone Number')}</Typography>}
                    secondary={<Typography color="text.secondary" sx={{ fontSize: '1.3rem' }}>{formatPhoneNumber(user.phoneNumber)}</Typography>}
                  />
                </ListItem>

                <Divider />

                <ListItem>
                  <ListItemIcon>
                    <CalendarIcon color="primary" sx={{ fontSize: '2rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{t('profilePage.memberSince', 'Member Since')}</Typography>}
                    secondary={<Typography color="text.secondary" sx={{ fontSize: '1.3rem' }}>{formatDate(user.createdAt)}</Typography>}
                  />
                </ListItem>

                {user.bio && (
                  <>
                    <Divider />
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon color="primary" sx={{ fontSize: '2rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{t('profilePage.bio', 'Bio')}</Typography>}
                        secondary={<Typography color="text.secondary" sx={{ fontSize: '1.3rem' }}>{user.bio}</Typography>}
                      />
                    </ListItem>
                  </>
                )}

                {(user as any)?.referralCode && (
                  <>
                    <Divider />
                    <ListItem
                      secondaryAction={
                        <Button
                          size="small"
                          startIcon={<FileCopyIcon />}
                          onClick={() => {
                            const link = `${window.location.origin}/invite?ref=${(user as any).referralCode}`;
                            navigator.clipboard.writeText(link);
                            alert(t('profilePage.linkCopied', 'Referral link copied to clipboard!'));
                          }}
                        >
                          {t('profilePage.copyLink', 'Copy Link')}
                        </Button>
                      }
                    >
                      <ListItemIcon>
                        <WalletIcon color="primary" sx={{ fontSize: '2rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{t('profilePage.referralLink', 'Referral Link')}</Typography>}
                        secondary={
                          <Typography color="text.secondary" sx={{ fontSize: '1.1rem', wordBreak: 'break-all' }}>
                            {`${window.location.origin}/invite?ref=${(user as any).referralCode}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </>
                )}

                {(user as any)?.languages && (user as any).languages.length > 0 && (
                  <>
                    <Divider />
                    <ListItem>
                      <ListItemIcon>
                        <PersonIcon color="primary" sx={{ fontSize: '2rem' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>{t('profilePage.languages', 'Languages')}</Typography>}
                        secondary={<Typography color="text.secondary" sx={{ fontSize: '1.3rem' }}>{(user as any).languages.join(', ')}</Typography>}
                      />
                    </ListItem>
                  </>
                )}
              </List>
            </Paper>
            {user.role === 'DRIVER' && (
              <Paper 
                elevation={2} 
                sx={{ p: 3, mb: 3 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontSize: '1.875rem', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <NotificationsIcon color="primary" sx={{ fontSize: '2.2rem' }} /> {t('profilePage.driverPreferences.title', '🚗 Driver Preferences')}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: '250px' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.4rem' }}>
                      {t('profilePage.driverPreferences.alertsLabel', 'Ride Request Alerts')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.2rem', mt: 0.5 }}>
                      {t('profilePage.driverPreferences.alertsDesc', 'Get notified when passengers post requests from your service cities')}
                    </Typography>
                  </Box>
                  {serviceAreaLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={rideRequestAlerts}
                          onChange={handleToggleAlerts}
                          color="primary"
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' } }}
                        />
                      }
                      label=""
                      sx={{ m: 0 }}
                    />
                  )}
                </Box>

                {rideRequestAlerts && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, fontSize: '1.4rem' }}>
                      {t('profilePage.driverPreferences.locationsLabel', 'Your Service Locations')}
                    </Typography>

                    {serviceCities.length === 0 ? (
                      <Box sx={{ p: 3, border: '1px dashed rgba(0,0,0,0.15)', borderRadius: '12px', textAlign: 'center', mb: 3 }}>
                        <LocationIcon sx={{ fontSize: '2.5rem', color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.2rem' }}>
                          {t('profilePage.driverPreferences.noLocations', 'No locations added yet. Add cities, airports, or stations you drive from.')}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                        {serviceCities.map((location) => (
                          <Chip
                            key={location.serviceAreaId}
                            icon={<LocationIcon sx={{ fontSize: '1.4rem !important' }} />}
                            label={location.locationName}
                            onDelete={() => handleRemoveCity(location.serviceAreaId, location.locationName)}
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '1.2rem', py: 2.2, px: 1, borderRadius: '16px' }}
                          />
                        ))}
                      </Box>
                    )}

                    <Button
                      variant="outlined"
                      onClick={() => setShowAddCityModal(true)}
                      sx={{ borderRadius: '12px', fontSize: '1.2rem', py: 1, px: 2, textTransform: 'none', fontWeight: 'bold' }}
                    >
                      {t('profilePage.driverPreferences.addLocation', 'Add a Location')}
                    </Button>
                  </Box>
                )}
              </Paper>
            )}
            {user.role === 'DRIVER' && (
              <Paper 
                id="payout-settings-section" 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  mb: 3,
                  border: searchParams.get('tab') === 'payouts' ? `2px solid ${theme.palette.primary.main}` : 'none',
                  animation: searchParams.get('tab') === 'payouts' ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(99, 102, 241, 0.4)' },
                    '70%': { boxShadow: '0 0 0 10px rgba(99, 102, 241, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(99, 102, 241, 0)' }
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontSize: '1.875rem' }}>
                  {t('profilePage.payoutSettings', '💰 Payout Settings')}
                </Typography>
                
                {payoutError && (
                  <Alert severity="error" sx={{ mb: 2, fontSize: '1.2rem' }}>
                    {payoutError}
                  </Alert>
                )}
                {payoutSuccess && (
                  <Alert severity="success" sx={{ mb: 2, fontSize: '1.2rem' }}>
                    {payoutSuccess}
                  </Alert>
                )}

                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.3rem', lineHeight: 1.6 }}>
                  {t('profilePage.payoutDescription', 'To receive passenger credit card payments directly to your bank account, you must securely link your account via Stripe Connect Express. Stripe manages security, verification, and transfers.')}
                </Typography>

                {user.stripeConnectOnboarded && (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      gap: 2, 
                      mb: 3, 
                      mt: 2, 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between' 
                    }}
                  >
                    <Paper 
                      variant="outlined" 
                      sx={{ p: 2, flex: 1, textAlign: 'center', bgcolor: 'rgba(40, 167, 69, 0.04)', borderColor: 'rgba(40, 167, 69, 0.2)' }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 0.5 }}>
                        💵 {t('profilePage.clearedPayouts', 'Cleared Payouts')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '1.8rem' }}>
                        ${stats.clearedPayouts.toFixed(2)}
                      </Typography>
                    </Paper>

                    <Paper 
                      variant="outlined" 
                      sx={{ p: 2, flex: 1, textAlign: 'center', bgcolor: 'rgba(99, 102, 241, 0.04)', borderColor: 'rgba(99, 102, 241, 0.2)' }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 0.5 }}>
                        ⏳ {t('profilePage.pendingEscrow', 'Pending Escrow')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.8rem' }}>
                        ${stats.pendingPayouts.toFixed(2)}
                      </Typography>
                    </Paper>

                    {stats.disputedPayouts > 0 && (
                      <Paper 
                        variant="outlined" 
                        sx={{ p: 2, flex: 1, textAlign: 'center', bgcolor: 'rgba(220, 53, 69, 0.04)', borderColor: 'rgba(220, 53, 69, 0.2)' }}
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 0.5 }}>
                          ⚠️ {t('profilePage.disputedPayouts', 'Disputed (Frozen)')}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'error.main', fontSize: '1.8rem' }}>
                          ${stats.disputedPayouts.toFixed(2)}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold' }}>
                      {t('profilePage.payoutStatus', 'Status:')}
                    </Typography>
                    <Chip
                      label={user.stripeConnectOnboarded ? t('profilePage.payoutActive', 'Active (Linked)') : t('profilePage.payoutInactive', 'Inactive (Not Linked)')}
                      color={user.stripeConnectOnboarded ? 'success' : 'warning'}
                      sx={{ fontSize: '1.1rem', fontWeight: 'bold', px: 1 }}
                    />
                    {user.stripeConnectAccountId && !user.stripeConnectOnboarded && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => checkPayoutStatus(true)}
                        disabled={payoutLoading}
                        sx={{ fontSize: '0.9rem', textTransform: 'none' }}
                      >
                        {payoutLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                        {t('profilePage.refreshStatus', 'Refresh Status')}
                      </Button>
                    )}
                  </Box>

                  {payoutLoading ? (
                    <CircularProgress size={30} />
                  ) : user.stripeConnectOnboarded ? (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleManagePayout}
                        sx={{ fontSize: '1.3rem', py: 1, px: 3, borderRadius: '12px' }}
                      >
                        {t('profilePage.managePayouts', 'Manage Payouts')}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleDisconnectPayout}
                        sx={{ fontSize: '1.3rem', py: 1, px: 3, borderRadius: '12px' }}
                      >
                        {t('profilePage.disconnectBankAccount', 'Disconnect Stripe Account')}
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleLinkPayout}
                        sx={{ fontSize: '1.3rem', py: 1, px: 3, borderRadius: '12px' }}
                      >
                        {t('profilePage.linkBankAccount', 'Link Bank Account')}
                      </Button>
                      {user.stripeConnectAccountId && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleDisconnectPayout}
                          sx={{ fontSize: '1.3rem', py: 1, px: 3, borderRadius: '12px' }}
                        >
                          {t('profilePage.disconnectBankAccount', 'Disconnect Stripe Account')}
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, fontSize: '1.875rem' }}>
                {t('profilePage.quickActions', 'Quick Actions')}
              </Typography>

              <List>
                {user.role === 'DRIVER' && (
                  <>
                    <ListItemButton onClick={() => navigate('/credits')} sx={{ py: 2 }}>
                      <ListItemIcon>
                        <WalletIcon sx={{ fontSize: '2rem' }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 600 }}>{t('profilePage.manageCredits', 'Manage Credits')}</Typography>} />
                    </ListItemButton>

                    <ListItemButton onClick={() => navigate('/vehicles')} sx={{ py: 2 }}>
                      <ListItemIcon>
                        <CarIcon sx={{ fontSize: '2rem' }} />
                      </ListItemIcon>
                      <ListItemText primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 600 }}>{t('profilePage.myVehicles', 'My Vehicles')}</Typography>} />
                    </ListItemButton>
                  </>
                )}

                <ListItemButton onClick={() => navigate('/help')} sx={{ py: 2 }}>
                  <ListItemIcon>
                    <HelpIcon sx={{ fontSize: '2rem' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 600 }}>{t('profilePage.helpFAQ', 'Help & FAQ')}</Typography>} />
                </ListItemButton>

                <ListItemButton onClick={() => navigate('/support')} sx={{ py: 2 }}>
                  <ListItemIcon>
                    <SupportIcon sx={{ fontSize: '2rem' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 600 }}>{t('profilePage.contactSupport', 'Contact Support')}</Typography>} />
                </ListItemButton>

                <Divider sx={{ my: 1.5 }} />

                <ListItemButton onClick={handleLogout} sx={{ color: 'error.main', py: 2 }}>
                  <ListItemIcon>
                    <LogoutIcon color="error" sx={{ fontSize: '2rem' }} />
                  </ListItemIcon>
                  <ListItemText primary={<Typography sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: 'error.main' }}>{t('profilePage.logout', 'Logout')}</Typography>} />
                </ListItemButton>
              </List>
            </Paper>
          </Box>
        </Box>

        {/* Edit Profile Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '32px',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }
          }}
        >
          <Box sx={{
            p: 4,
            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box>
              <Typography sx={{ fontSize: '2.5rem', fontWeight: '900', letterSpacing: '-0.02em' }}>
                {t('profilePage.editDialog.title', 'Edit Profile')} ✏️
              </Typography>
              <Typography sx={{ fontSize: '1.2rem', opacity: 0.9, fontWeight: 500 }}>
                Flex your best self ⚡
              </Typography>
            </Box>
            <IconButton
              onClick={() => setEditDialogOpen(false)}
              sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
            >
              <CloseIcon sx={{ fontSize: '2.5rem' }} />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 2 }}>
              <Alert
                severity="warning"
                sx={{ borderRadius: '16px', fontSize: '1.2rem', fontWeight: 600 }}
              >
                {t('profilePage.editDialog.nameLocked', 'Legal names are locked for platform safety. Contact support to change them.')}
              </Alert>

              <TextField
                fullWidth
                disabled
                label={t('profilePage.editDialog.firstName', 'First Name')}
                value={editForm.firstName}
                InputProps={{ sx: { fontSize: '2.1rem', borderRadius: '16px', fontWeight: '800', bgcolor: 'rgba(0,0,0,0.02)' } }}
                InputLabelProps={{ sx: { fontSize: '1.8rem', fontWeight: '700' } }}
              />

              <TextField
                fullWidth
                disabled
                label={t('profilePage.editDialog.lastName', 'Last Name')}
                value={editForm.lastName}
                InputProps={{ sx: { fontSize: '2.1rem', borderRadius: '16px', fontWeight: '800', bgcolor: 'rgba(0,0,0,0.02)' } }}
                InputLabelProps={{ sx: { fontSize: '1.8rem', fontWeight: '700' } }}
              />

              <TextField
                fullWidth
                label={t('profilePage.editDialog.preferredName', 'Preferred Name / Nickname')}
                value={editForm.preferredName}
                onChange={(e) => handleFormChange('preferredName', e.target.value)}
                placeholder={t('profilePage.editDialog.preferredNamePlaceholder', 'How should we call you?')}
                InputProps={{ sx: { fontSize: '2.1rem', borderRadius: '16px', fontWeight: '800' } }}
                InputLabelProps={{ sx: { fontSize: '1.8rem', fontWeight: '700' } }}
                helperText={t('profilePage.editDialog.preferredNameHelper', 'This is the name other users will see.')}
              />

              <TextField
                fullWidth
                label={t('profilePage.editDialog.bio', 'Bio (Optional)')}
                value={editForm.bio}
                onChange={(e) => handleFormChange('bio', e.target.value)}
                error={!!editErrors.bio}
                helperText={editErrors.bio || t('profilePage.editDialog.bioHelper', '{{current}}/500 characters', { current: editForm.bio.length })}
                multiline
                rows={4}
                InputProps={{ sx: { fontSize: '1.8rem', borderRadius: '20px', fontWeight: '600', lineHeight: 1.6 } }}
                InputLabelProps={{ sx: { fontSize: '1.8rem', fontWeight: '700' } }}
              />

              {/* Bio AI Panel */}
              <Box sx={{
                mt: -1,
                p: 3,
                borderRadius: '24px',
                bgcolor: '#f8fafc',
                border: '2px dashed #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '1.3rem', fontWeight: '800', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                    ✨ {t('profile.editProfileScreen.bioPreview', 'Bio Preview & Translation')}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleCheckBioModeration}
                    disabled={moderationStatus === 'checking' || !editForm.bio.trim()}
                    startIcon={moderationStatus === 'checking' ? <CircularProgress size={16} /> : (moderationStatus === 'approved' ? <ShieldIcon /> : <PolicyIcon />)}
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontWeight: '800',
                      fontSize: '1rem',
                      borderColor: moderationStatus === 'approved' ? 'success.main' : (moderationStatus === 'flagged' ? 'error.main' : 'primary.main'),
                      color: moderationStatus === 'approved' ? 'success.main' : (moderationStatus === 'flagged' ? 'error.main' : 'primary.main'),
                      '&:hover': {
                        borderColor: moderationStatus === 'approved' ? 'success.dark' : (moderationStatus === 'flagged' ? 'error.dark' : 'primary.dark'),
                        bgcolor: moderationStatus === 'approved' ? 'success.light' : (moderationStatus === 'flagged' ? 'error.light' : 'primary.light'),
                        opacity: 0.1
                      }
                    }}
                  >
                    {moderationStatus === 'checking' ? 'Checking...' : (moderationStatus === 'approved' ? t('profile.editProfileScreen.bioApproved') : (moderationStatus === 'flagged' ? t('profile.editProfileScreen.bioFlagged') : t('profile.editProfileScreen.checkBio')))}
                  </Button>
                </Box>

                {moderationStatus === 'flagged' && (
                  <Alert severity="error" sx={{ borderRadius: '12px' }}>
                    {moderationReason}
                  </Alert>
                )}

                <Divider sx={{ borderStyle: 'dashed' }} />

                <Box sx={{ minHeight: '60px', py: 1 }}>
                  <TranslatingText
                    text={editForm.bio || '...'}
                    variant="body2"
                    sx={{
                      '& .MuiTypography-root': {
                        fontSize: '1.4rem',
                        lineHeight: 1.6,
                        color: 'text.secondary',
                        fontWeight: 500
                      }
                    }}
                  />
                </Box>
              </Box>

              <Autocomplete
                multiple
                options={LANGUAGE_OPTIONS}
                value={editForm.languages}
                onChange={(_, value) => handleFormChange('languages', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('profilePage.editDialog.languages', 'Languages')}
                    placeholder={t('profilePage.editDialog.languagesPlaceholder', 'Select languages you speak')}
                    InputProps={{
                      ...params.InputProps,
                      sx: { fontSize: '1.8rem', borderRadius: '16px', fontWeight: '800' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '1.8rem', fontWeight: '700' } }}
                  />
                )}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props as any;
                  return (
                    <li key={key} {...optionProps} style={{ fontSize: '1.6rem', fontWeight: '700' }}>
                      <Checkbox
                        checked={selected}
                        sx={{ mr: 1, '& .MuiSvgIcon-root': { fontSize: '2.5rem' } }}
                      />
                      {option}
                    </li>
                  );
                }}
              />

              <TextField
                fullWidth
                label={t('profilePage.phoneNumber', 'Phone Number')}
                value={editForm.phoneNumber}
                onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
                placeholder="+1 (234) 567-8900"
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 2, color: 'primary.main', fontSize: '2.5rem' }} />,
                  sx: { fontSize: '1.8rem', borderRadius: '16px', fontWeight: '600' }
                }}
                InputLabelProps={{ sx: { fontSize: '1.8rem', fontWeight: '700' } }}
              />

              <Alert
                severity="info"
                icon={<InfoIcon sx={{ fontSize: '2.5rem' }} />}
                sx={{
                  borderRadius: '20px',
                  fontSize: '1.4rem',
                  fontWeight: '600',
                  bgcolor: 'rgba(99, 102, 241, 0.05)',
                  color: '#6366f1',
                  '& .MuiAlert-message': { fontSize: '1.3rem', lineHeight: 1.5 }
                }}
              >
                {t('profilePage.editDialog.changeInfoEmail', '💡 To change your email address, please contact support.')}
              </Alert>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 4, pt: 0, gap: 2, justifyContent: 'center' }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              sx={{
                fontSize: '1.6rem',
                px: 4,
                py: 1.5,
                borderRadius: '20px',
                fontWeight: '900',
                textTransform: 'none'
              }}
            >
              {t('profilePage.editDialog.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleSaveProfile}
              variant="contained"
              sx={{
                fontSize: '1.8rem',
                px: 6,
                py: 1.5,
                borderRadius: '20px',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                boxShadow: '0 10px 20px rgba(99, 102, 241, 0.3)',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
                }
              }}
            >
              {t('profilePage.editDialog.save', 'Save Changes')} 🚀
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Location Modal */}
        <Dialog
          open={showAddCityModal}
          onClose={() => {
            setShowAddCityModal(false);
            setSelectedNewLocation(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '24px', p: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
              📍 {t('profilePage.driverPreferences.addLocationModalTitle', 'Add Service Location')}
            </Typography>
            <IconButton onClick={() => {
              setShowAddCityModal(false);
              setSelectedNewLocation(null);
            }}>
              <CloseIcon sx={{ fontSize: '2.5rem' }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3, pt: 0 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.3rem' }}>
              {t('profilePage.driverPreferences.addLocationModalDesc', 'Search and select any city, airport, or region to add to your alerts service area.')}
            </Typography>
            <Box sx={{ minHeight: '150px' }}>
              <LocationAutocomplete
                label={t('profilePage.driverPreferences.locationLabel', 'Location')}
                placeholder={t('profilePage.driverPreferences.locationPlaceholder', 'Type a city or airport...')}
                icon={<LocationIcon sx={{ fontSize: '2rem', color: 'primary.main' }} />}
                value={selectedNewLocation}
                onChange={(loc) => setSelectedNewLocation(loc)}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => {
                setShowAddCityModal(false);
                setSelectedNewLocation(null);
              }}
              sx={{ fontSize: '1.4rem', textTransform: 'none', fontWeight: 'bold' }}
            >
              {t('profilePage.driverPreferences.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleAddCity}
              variant="contained"
              disabled={addingCity || !selectedNewLocation}
              sx={{ fontSize: '1.4rem', borderRadius: '12px', px: 3, py: 1, textTransform: 'none', fontWeight: 'bold' }}
            >
              {addingCity ? <CircularProgress size={20} /> : t('profilePage.driverPreferences.add', 'Add Location')}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
};

export default ProfilePage;